import { JBaseApi } from '../base';
import {
    AddCommentParams,
    ApiWrapper,
    BaseMessages,
    UpdateCommentParams,
    FormatList,
    jArticleList,
    jArticleLittle,
    jFileLittle,
    MaybeWithToken,
    PagingResults,
    UserRead,
} from '../../types';
import { OF, combineFormat } from '../../util';
import { ArticleCreation, ArticleGetConfig, ArticleListConfig, GetCommentsParams } from '../../types/article';
import { jCommentLittle } from '../../types/comment';
import { FormatExtension } from '../../types/extensions';

export type DraftSerializedForm = {
    name: string;
    value: unknown;
}[];

export class JArticleApi extends JBaseApi {
    public list<T extends jArticleLittle = jArticleLittle>(
        config: ArticleListConfig,
    ): Promise<ApiWrapper<PagingResults<T>>> {
        return this.network.post({
            o: 'objectList',
            f: 'jamespotArticleQueryList',
            ...config,
            format: combineFormat(config.format ?? 'raw-list', config.formatExtend ?? []),
        } as OF);
    }

    public get<T extends jArticleLittle = jArticleLittle>(
        config: ArticleGetConfig,
        params?: { signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiWrapper<T, BaseMessages>> {
        return this.network.post(
            {
                o: 'article',
                f: 'get',
                ...config,
                format: combineFormat(config.format ?? 'raw-view', params?.formatExtension),
            } as OF,
            params?.signal,
        );
    }

    public create<R extends jArticleLittle = jArticleLittle, P extends ArticleCreation = ArticleCreation>(
        article: MaybeWithToken<P>,
        format?: FormatList,
    ): Promise<ApiWrapper<Array<R>, BaseMessages>> {
        return this.network.maybePostToken({
            o: 'article',
            f: 'create',
            format: format ?? 'raw-list',
            ...article,
        } as OF);
    }

    public update<T extends { uri: string }, U extends jArticleLittle = jArticleList>(
        article: T,
        options?: { format?: FormatList },
    ): Promise<ApiWrapper<U, BaseMessages>> {
        return this.network.post({
            format: options?.format ?? 'raw-list',
            ...article,
            o: 'article',
            f: 'update',
        } as OF);
    }

    public delete(id: number): Promise<ApiWrapper<boolean, BaseMessages>> {
        return this.network.postCSRF({
            o: 'article',
            f: 'delete',
            idArticle: id,
        } as OF);
    }

    public count(type: string): Promise<ApiWrapper<number>> {
        return this.network.post({
            o: 'article',
            f: 'count',
            type,
        } as OF);
    }

    public getAttachments(id: number, type: string, format?: FormatList): Promise<ApiWrapper<jFileLittle[]>> {
        return this.network.post({
            o: 'article',
            f: 'getAttachment',
            idArticle: id,
            type,
            format,
        } as OF);
    }

    public permalink(permalinkId: string): Promise<ApiWrapper<jArticleLittle>> {
        return this.network.post({
            o: 'article',
            f: 'permalink',
            permalinkId,
        } as OF);
    }

    public saveAsDraft(params: {
        form: Record<string, unknown>;
        contentType: string;
        idDraft?: string;
        idFrom?: number;
    }): Promise<ApiWrapper<{ idDraft: string; msg: string }>> {
        return this.network.post({
            o: 'article',
            f: 'saveAsDraft',
            ...params,
        } as OF);
    }

    public getComments<T extends jCommentLittle = jCommentLittle>(
        params: GetCommentsParams,
        options?: { format?: FormatList; signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiWrapper<T[]>> {
        return this.network.post(
            {
                o: 'article',
                f: 'getComments',
                ...params,
                attachments: params.attachment ?? false,
                limits: params.limits ?? { start: 0, max: 10000 },
                format: combineFormat(options?.format ?? 'raw-little', options?.formatExtension),
            } as OF,
            options?.signal,
        );
    }

    public addComment<T extends jCommentLittle = jCommentLittle>(
        params: AddCommentParams,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.network.post({
            o: 'article',
            f: 'addComment',
            ...params,
            alertAuthor: Number(params.alertAuthor),
            sendAlert: Number(params.sendAlert),
            type: params.type ?? 'comment',
            format: format ?? 'raw-little',
        } as OF);
    }

    public updateComment<T extends jCommentLittle = jCommentLittle>(
        params: UpdateCommentParams,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.network.post({
            o: 'article',
            f: 'updateComment',
            ...params,
            format: format ?? 'raw-little',
        } as OF);
    }

    public getComment<T extends jCommentLittle = jCommentLittle>(
        params: { idComment: number | string },
        options?: { format?: FormatList; signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiWrapper<T>> {
        return this.network.post({
            o: 'comment',
            f: 'getComment',
            ...params,
            format: combineFormat(options?.format ?? 'raw-little', options?.formatExtension),
        } as OF);
    }

    public deleteComment(idComment: number): Promise<ApiWrapper<void>> {
        return this.network.postCSRF({
            o: 'comment',
            f: 'delete',
            idComment,
        } as OF);
    }

    public getRead(uri: string): Promise<ApiWrapper<{ count: number; list: UserRead[] }>> {
        return this.network.post({
            o: 'article',
            f: 'getRead',
            uri,
        } as OF);
    }
}
